import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle2,
  FileSignature,
  Plus,
  ScanLine,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ErrorPanel, Loading } from "../components/Loading";
import { EntityDrawer, PageHeader } from "../components/ui";
import { api, ApiError } from "../lib/api";
import type { ProviderInvoice } from "../lib/api";
import { useLegalEntityGate } from "../lib/legalEntity";

const STATUS_TABS = [
  "draft",
  "ocr_done",
  "matched",
  "discrepancy",
  "resolved",
] as const;

function StatusPill({ status }: { status: string }) {
  const cls =
    status === "matched"
      ? "bg-emerald-100 text-emerald-800"
      : status === "discrepancy"
      ? "bg-rose-100 text-rose-800"
      : status === "resolved"
      ? "bg-brand-blue/15 text-brand-deep"
      : "bg-slate-100 text-slate-700";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${cls}`}
    >
      {status.replace(/_/g, " ")}
    </span>
  );
}

function NewInvoiceDialog({
  legalEntityId,
  onClose,
  onCreated,
}: {
  legalEntityId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const today = new Date();
  const [form, setForm] = useState({
    plan_id: "",
    billing_period_year: today.getFullYear(),
    billing_period_month: today.getMonth() + 1,
    document_url: "",
    tolerance: "0.01",
  });
  const [error, setError] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: () =>
      api.createProviderInvoice({
        legal_entity_id: legalEntityId,
        plan_id: form.plan_id,
        billing_period_year: form.billing_period_year,
        billing_period_month: form.billing_period_month,
        document_url: form.document_url || null,
        tolerance: form.tolerance,
      }),
    onSuccess: () => {
      onCreated();
      onClose();
    },
    onError: (e: unknown) => {
      setError(e instanceof ApiError ? e.detail : String(e));
    },
  });
  return (
    <EntityDrawer
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={t("benefits.invoice_new", "Upload a provider invoice")}
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-border-subtle px-4 py-2 text-sm font-medium text-text-secondary hover:bg-surface-sunken"
          >
            {t("common.cancel", "Cancel")}
          </button>
          <button
            type="button"
            onClick={() => {
              setError(null);
              mutation.mutate();
            }}
            disabled={!form.plan_id}
            className="rounded-lg bg-brand-deep px-4 py-2 text-sm font-medium text-white hover:opacity-95 disabled:opacity-50"
          >
            {t("common.create", "Create")}
          </button>
        </div>
      }
    >
      <div className="space-y-3 p-4 text-sm">
        <div>
          <label className="block text-xs font-medium text-text-secondary">
            {t("benefits.invoice_plan_id", "Plan ID (UUID)")}
          </label>
          <input
            value={form.plan_id}
            onChange={(e) => setForm({ ...form, plan_id: e.target.value })}
            className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2 font-mono text-xs"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-text-secondary">
              {t("benefits.invoice_year", "Year")}
            </label>
            <input
              type="number"
              value={form.billing_period_year}
              onChange={(e) =>
                setForm({
                  ...form,
                  billing_period_year: parseInt(e.target.value || "0", 10),
                })
              }
              className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2 font-mono"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-text-secondary">
              {t("benefits.invoice_month", "Month")}
            </label>
            <input
              type="number"
              min={1}
              max={12}
              value={form.billing_period_month}
              onChange={(e) =>
                setForm({
                  ...form,
                  billing_period_month: parseInt(e.target.value || "0", 10),
                })
              }
              className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2 font-mono"
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-text-secondary">
            {t("benefits.invoice_url", "Document URL")}
          </label>
          <input
            value={form.document_url}
            onChange={(e) =>
              setForm({ ...form, document_url: e.target.value })
            }
            placeholder="https://r2.solvait/.../invoice.pdf"
            className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2 font-mono text-xs"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-text-secondary">
            {t("benefits.invoice_tolerance", "Tolerance (SAR)")}
          </label>
          <input
            value={form.tolerance}
            onChange={(e) => setForm({ ...form, tolerance: e.target.value })}
            className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2 font-mono"
          />
        </div>
        {error && (
          <div className="rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-800">
            {error}
          </div>
        )}
      </div>
    </EntityDrawer>
  );
}

function InvoiceRow({
  inv,
  onOCR,
  onReconcile,
  onResolve,
}: {
  inv: ProviderInvoice;
  onOCR: () => void;
  onReconcile: (actual: string) => void;
  onResolve: (notes: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="rounded-2xl border border-border-subtle bg-white p-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-sm font-medium text-text-primary">
            {inv.provider_invoice_number ?? inv.id.slice(0, 8)}
          </div>
          <div className="text-xs text-text-tertiary">
            {inv.billing_period_year}-
            {String(inv.billing_period_month).padStart(2, "0")} ·{" "}
            {inv.plan_id.slice(0, 8)}…
          </div>
        </div>
        <StatusPill status={inv.status} />
      </div>
      <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-text-secondary">
        <div>
          <dt className="text-text-tertiary">
            {t("benefits.invoice_expected", "Expected")}
          </dt>
          <dd className="font-mono">{inv.expected_total}</dd>
        </div>
        <div>
          <dt className="text-text-tertiary">
            {t("benefits.invoice_actual", "Actual")}
          </dt>
          <dd className="font-mono">{inv.actual_total ?? "—"}</dd>
        </div>
        <div>
          <dt className="text-text-tertiary">
            {t("benefits.invoice_tolerance_short", "Tolerance")}
          </dt>
          <dd className="font-mono">{inv.tolerance}</dd>
        </div>
        <div>
          <dt className="text-text-tertiary">
            {t("benefits.invoice_ocr", "OCR")}
          </dt>
          <dd>{inv.ocr_status.replace(/_/g, " ")}</dd>
        </div>
        {inv.resolution_notes && (
          <div className="col-span-2">
            <dt className="text-text-tertiary">
              {t("benefits.invoice_resolution", "Resolution")}
            </dt>
            <dd className="text-text-primary">{inv.resolution_notes}</dd>
          </div>
        )}
      </dl>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {inv.document_url && inv.ocr_status === "pending" && (
          <button
            type="button"
            onClick={onOCR}
            className="rounded-lg bg-brand-deep px-2 py-1 text-[11px] font-medium text-white hover:opacity-95"
          >
            <ScanLine className="mr-1 inline h-3 w-3" />
            {t("benefits.invoice_ocr_run", "Run OCR")}
          </button>
        )}
        {(inv.status === "draft" || inv.status === "ocr_done") && (
          <button
            type="button"
            onClick={() => {
              const v = window.prompt(
                t(
                  "benefits.invoice_actual_prompt",
                  "Enter actual total from invoice (SAR):",
                ),
                String(inv.expected_total),
              );
              if (v && v.trim().length > 0) onReconcile(v.trim());
            }}
            className="rounded-lg border border-brand-deep px-2 py-1 text-[11px] font-medium text-brand-deep hover:bg-surface-sunken"
          >
            <FileSignature className="mr-1 inline h-3 w-3" />
            {t("benefits.invoice_reconcile", "Reconcile")}
          </button>
        )}
        {inv.status === "discrepancy" && (
          <button
            type="button"
            onClick={() => {
              const notes = window.prompt(
                t(
                  "benefits.invoice_resolve_prompt",
                  "Resolution notes (audit-logged):",
                ),
              );
              if (notes && notes.trim().length > 0) onResolve(notes);
            }}
            className="rounded-lg bg-emerald-700 px-2 py-1 text-[11px] font-medium text-white hover:opacity-95"
          >
            <CheckCircle2 className="mr-1 inline h-3 w-3" />
            {t("benefits.invoice_resolve", "Resolve")}
          </button>
        )}
      </div>
    </div>
  );
}

export function ProviderInvoicesPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { legalEntityId, gate } = useLegalEntityGate();
  const [statusTab, setStatusTab] = useState<string>("");
  const [createOpen, setCreateOpen] = useState(false);

  const list = useQuery({
    queryKey: ["provider-invoices", legalEntityId, statusTab],
    queryFn: () =>
      api.listProviderInvoices(legalEntityId ?? "", {
        status: statusTab || undefined,
      }),
    enabled: !!legalEntityId,
  });

  const ocr = useMutation({
    mutationFn: (id: string) => api.runProviderInvoiceOCR(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["provider-invoices"] });
    },
  });
  const reconcile = useMutation({
    mutationFn: ({ id, actual }: { id: string; actual: string }) =>
      api.reconcileProviderInvoice(id, { actual_total: actual }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["provider-invoices"] });
    },
  });
  const resolve = useMutation({
    mutationFn: ({ id, notes }: { id: string; notes: string }) =>
      api.resolveProviderInvoice(id, {
        resolver_clerk_user_id: "hr_director",
        notes,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["provider-invoices"] });
    },
  });

  if (gate) return gate;

  return (
    <div className="space-y-4">
      <PageHeader
        title={t("benefits.invoices_title", "Provider invoices")}
        subtitle={t(
          "benefits.invoices_subtitle",
          "Monthly per-plan invoices. Sonnet OCR extracts amounts; expected total is computed from active enrollments × tier premiums.",
        )}
      />
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-lg bg-brand-deep px-3 py-1.5 text-sm font-medium text-white hover:opacity-95"
        >
          <Plus className="h-4 w-4" />
          {t("benefits.invoice_new", "Upload invoice")}
        </button>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-border-subtle">
        <button
          type="button"
          onClick={() => setStatusTab("")}
          className={`-mb-px rounded-t-lg border-b-2 px-3 py-1 text-xs ${
            statusTab === ""
              ? "border-brand-blue text-brand-deep"
              : "border-transparent text-text-secondary hover:text-text-primary"
          }`}
        >
          {t("benefits.all", "All")}
        </button>
        {STATUS_TABS.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStatusTab(s)}
            className={`-mb-px rounded-t-lg border-b-2 px-3 py-1 text-xs capitalize ${
              statusTab === s
                ? "border-brand-blue text-brand-deep"
                : "border-transparent text-text-secondary hover:text-text-primary"
            }`}
          >
            {s.replace(/_/g, " ")}
          </button>
        ))}
      </div>

      <div className="rounded-xl border border-amber-300 bg-amber-50/60 p-3 text-xs text-amber-900 flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
        <div>
          {t(
            "benefits.invoice_note",
            "Provider integration is RECORD-ONLY in v1. Actual claims processing happens via provider portals; this surface tracks invoice receipt + reconciliation.",
          )}
        </div>
      </div>

      {list.isLoading && <Loading />}
      {list.isError && (
        <ErrorPanel message={(list.error as Error).message} />
      )}
      {list.data && list.data.length === 0 && (
        <div className="rounded-2xl border border-dashed border-border-subtle p-6 text-center text-sm text-text-tertiary">
          {t(
            "benefits.invoices_empty",
            "No invoices yet — upload the first one to start reconciliation.",
          )}
        </div>
      )}
      {list.data && list.data.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {list.data.map((inv) => (
            <InvoiceRow
              key={inv.id}
              inv={inv}
              onOCR={() => ocr.mutate(inv.id)}
              onReconcile={(actual) =>
                reconcile.mutate({ id: inv.id, actual })
              }
              onResolve={(notes) =>
                resolve.mutate({ id: inv.id, notes })
              }
            />
          ))}
        </div>
      )}

      {createOpen && legalEntityId && (
        <NewInvoiceDialog
          legalEntityId={legalEntityId}
          onClose={() => setCreateOpen(false)}
          onCreated={() =>
            qc.invalidateQueries({ queryKey: ["provider-invoices"] })
          }
        />
      )}
    </div>
  );
}
